import {
  clientVerificationRequiredAtom,
  paymentMethodsAtom,
  payWithApplePayAtom,
  payWithGooglePayAtom,
  statusAtom,
  userInfoAtom,
  verifyComponentAtom,
  verifyStepAtom,
} from '@/state/global'
import { PaymentData, PaymentDataAtom, switchedPaymentMethodAtom } from '@/state/payment'
import { API_URL, RELEASE_TAG } from '@/utils/constants'
import { deduplicatePaymentMethods, magensaDuplicateMatch } from '@/utils/fortis.utils'
import { buildMagensaPaymentMethod } from '@/utils/magensa-component.utils'
import { newRelicLog } from '@/utils/newrelic'
import { verifyResend } from '@/utils/apis'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import React, { useEffect, useRef, useState } from 'react'
import ErrorModal from '../ErrorModal'
import { VerifyStep } from '../verify/types'
import { useAuthQuery } from '@/hooks/useAuthQuery'
import { isUnauthorizedError } from '@/utils/authErrorHandler'

const MagensaUnigateHostedPage = ({
  closePage,
  hasRunningTab = false,
}: {
  closePage: (paymentMethod?: PaymentData | undefined) => void
  hasRunningTab?: boolean
}) => {
  const [isVerifyComponentOpen, openVerifyComponent] = useAtom(verifyComponentAtom)
  const [verifyStep, setVerifyStep] = useAtom(verifyStepAtom)
  const verifyStatus = useAtomValue(statusAtom)
  const user = useAtomValue(userInfoAtom)
  const [paymentMethods, setPaymentMethods] = useAtom(paymentMethodsAtom)
  const setPaymentMethod = useSetAtom(PaymentDataAtom)
  const setDidSwitchPaymentMethod = useSetAtom(switchedPaymentMethodAtom)
  const setPayWithApplePay = useSetAtom(payWithApplePayAtom)
  const setPayWithGooglePay = useSetAtom(payWithGooglePayAtom)
  const clientVerificationRequired = useAtomValue(clientVerificationRequiredAtom)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [reloadCounter, setReloadCounter] = useState(0)
  const [openErrorModal, setOpenErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState({
    title: 'Card Declined',
    description: 'Please Try Again',
    primaryButton: { name: 'Try Again', action: () => {} },
  })

  const iframeRef = useRef<any>(null)

  useEffect(() => {
    if (user?.require_sms_verification && verifyStatus === 'not_verified' && clientVerificationRequired) {
      openVerifyComponent(true)
      setVerifyStep(VerifyStep.Code)
    }
  }, [])

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [blobUrl])

  const { refetch: refetch } = useAuthQuery<boolean>(['magensa-unigate-hosted-page-payload'], () => fetchData(), {
    refetchOnWindowFocus: false,
    retry: false,
  })

  const fetchData = async () => {
    if (!user?.token) {
      throw { name: 'Unauthorized' }
    }

    try {
      const rawRes = await fetch(`${API_URL}/mobile/magensa-unigate-hosted-page-payload`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Build-Version': RELEASE_TAG,
          'X-Client-ID': user?.token ?? '',
          Platform: 'browser',
        },
      })

      const data = await rawRes.json()

      if (!rawRes.ok) {
        throw data
      }

      if (data?.hosted_page_html) {
        const html = atob(data.hosted_page_html)
        const blob = new Blob([html], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        setBlobUrl((prevUrl) => {
          if (prevUrl) URL.revokeObjectURL(prevUrl)
          return url
        })
      } else {
        setLoading(false)
      }
      return rawRes.ok
    } catch (err) {
      newRelicLog(
        {
          name: 'webclip-error',
          errorDomain: 'magensa unigate hosted page',
          errorMessage: 'Failed to fetch hosted page',
          errorDescription: JSON.stringify(err),
        },
        'pageAction'
      )
      if (isUnauthorizedError(err)) {
        try {
          await verifyResend(user)
        } catch {}
        ;(err as any)._verifyResendHandled = true
        setVerifyStep(VerifyStep.Code)
        openVerifyComponent(true)
      } else {
        setErrorMessage({
          title: 'Something Went Wrong',
          description: 'Unable to load the payment form.',
          primaryButton: {
            name: 'Try Again',
            action: () => {
              setOpenErrorModal(false)
              setLoading(true)
              refetch()
            },
          },
        })
        setOpenErrorModal(true)
      }
      setLoading(false)

      throw err
    }
  }

  useEffect(() => {
    const needsVerification = clientVerificationRequired && user?.require_sms_verification && verifyStatus === 'not_verified'
    if (needsVerification) return
    refetch()
  }, [user, verifyStatus, clientVerificationRequired])

  const handleTriggerSubmit = () => {
    setSubmitting(true)
    if (iframeRef?.current) {
      iframeRef.current.contentWindow.postMessage('triggerSubmit', '*')
    }
  }

  useEffect(() => {
    const handleMessage = (event: any) => {
      if (event.data.type === 'magensaReady') {
        setTimeout(() => setLoading(false), 1000)
      }

      if (event.data.type === 'magensaDone') {
        const data = event.data.data
        const paymentMethod: PaymentData = buildMagensaPaymentMethod(data, user)

        const newPayments = deduplicatePaymentMethods(paymentMethods, paymentMethod, magensaDuplicateMatch)
        localStorage.setItem('paymentMethods', JSON.stringify(newPayments))
        setPaymentMethods(newPayments)

        setPaymentMethod(paymentMethod)
        setPayWithApplePay(false)
        setPayWithGooglePay(false)

        if (hasRunningTab) {
          setDidSwitchPaymentMethod(true)
        }

        closePage(paymentMethod)
      }

      if (event.data.type === 'magensaError') {
        const errorData = event.data.data
        setErrorMessage({
          ...errorMessage,
          description: errorData?.error || 'An error occurred. Please try again.',
        })
        newRelicLog(
          {
            name: 'webclip-error',
            errorDomain: 'magensa unigate hosted page',
            errorMessage: 'Payment error',
            errorDescription: JSON.stringify(errorData),
          },
          'pageAction'
        )
        setOpenErrorModal(true)
        setSubmitting(false)
        setLoading(false)
        setReloadCounter((prev) => prev + 1)
        refetch()
      }
    }

    window.addEventListener('message', handleMessage)
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [paymentMethods, user, hasRunningTab])

  const handleCloseErrorModal = () => {
    setOpenErrorModal(false)
  }

  return (
    <div className="z-50 bg-white fixed inset-0 p-2 flex flex-col items-center text-black mx-auto max-w-[820px]">
      <ErrorModal
        isOpen={openErrorModal}
        close={handleCloseErrorModal}
        title={errorMessage.title}
        description={errorMessage.description}
        primaryButton={errorMessage.primaryButton}
        entities={[]}
      />
      <div className="flex justify-center relative items-center text-sm tracking-tight w-full mt-2">
        <p>PAYMENT METHODS</p>
        <button onClick={() => closePage()} className="absolute right-1 text-sm">
          Cancel
        </button>
      </div>
      <div className="mt-8 px-3 w-full">
        <h1 className="uppercase text-2xl">Add Card</h1>
        <p className="text-sm text-gray-600 mt-4 leading-tight">
          Your card info is completely encrypted and tokenized, and never stored in Union.
        </p>
      </div>
      {loading ? (
        <div className="animate-pulse h-full w-full">
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
          <div className="w-full h-12 bg-gray-300 rounded-md my-1" />
        </div>
      ) : null}
      {blobUrl && (
        <iframe
          src={blobUrl}
          ref={iframeRef}
          width="100%"
          height="60%"
          title="Magensa Unigate Checkout"
          key={reloadCounter}
          style={{ visibility: loading ? 'hidden' : 'visible' }}
        />
      )}
      <button
        className="h-[75px] py-7 text-center bg-primary-blue w-full flex items-center justify-center fixed bottom-0 mx-auto max-w-[820px]"
        onClick={handleTriggerSubmit}
        disabled={loading || submitting}
      >
        {submitting ? (
          <span className="text-white">
            <svg
              width="45"
              height="50"
              viewBox="0 0 145 50"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="animate-spin"
            >
              <path
                d="M121.408 0C109.718 0 103.134 9.99898 99.194 13.9392C90.239 22.8943 88.1307 2.0699 80.5854 2.0699H72.3979H42.7182V24.1889C42.7182 29.7667 40.3797 32.9137 35.7026 32.9137C31.7444 32.9137 29.6771 30.4831 29.6771 26.3484V2.06478H0V30.3935C0 43.2556 7.82161 50 23.0273 50C34.7175 50 41.3008 40.001 45.241 36.0608C54.1961 27.1057 56.3044 47.9327 63.8497 47.9327H101.717V25.8111C101.717 20.2333 104.055 17.0863 108.732 17.0863C112.691 17.0863 114.758 19.5169 114.758 23.6516V47.9352H144.438V19.6065C144.43 6.74445 136.608 0 121.408 0Z"
                fill="white"
              />
            </svg>
          </span>
        ) : (
          <span className="text-white">Save</span>
        )}
      </button>
    </div>
  )
}

export default MagensaUnigateHostedPage
